// src/App.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import lightTheme from './themes/lightTheme';
import darkTheme from './themes/darkTheme';
import Navbar from './components/Navbar';
import { Route, Routes, useLocation, Navigate, useParams } from 'react-router-dom';
import ProblemsPage from './pages/ProblemsPage';
import SavedProblemsPage from './pages/SavedProblemsPage';
import ProblemPage from './pages/ProblemPage';
import ProgramsPage from './pages/ProgramsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ContestsPage from './pages/ContestsPage';
import UserProfilePage from './pages/UserProfilePage';
import { Box, Fab, Tooltip, useMediaQuery, useTheme, alpha } from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import FeedbackForm from './pages/FeedbackFormModal';
import ProblemFormPage from './pages/admin/ProblemFormPage ';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ManageProblems from './pages/admin/ManageProblems';
import { login } from './redux/userSlice';
import ResetPassword from './components/auth/ResetPassword';
import GithubCallback from './components/auth/GithubCallback';
import Footer from './components/Footer';
import ManageContests from './pages/admin/ManageContests';
import LiveContest from './pages/LiveContest';
import ContestResult from './pages/ContestResult';
import ManageUsers from './pages/admin/ManageUsers';
import ManageProblemFeedback from './pages/admin/ManageProblemFeedback';
import ManageCareers from './pages/admin/ManageCareers';
import AdminBroadcastEmail from './pages/admin/AdminBroadcastEmail';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import CareersPage from './pages/CareersPage';
import BookingPage from './pages/BookingPage';
import DSACoursePage from './pages/AllProgramsPage';
import CompilerPage from './pages/CompilerPage';
import ChallengeDetailsPage from './pages/challenges/75HardChallengePage';
import LiveCodingRoom from './pages/live-coding/LiveCodingRoom';
import ContestInfoPage from './pages/contests/ContestInfoPage';
import LiveCodingPromoModal from './components/LiveCodingPromoModal';
import ProductTour from './components/ProductTour';
import ProblemsPageTour from './components/ProblemsPageTour';
import ContestsPageTour from './components/ContestsPageTour';
import ProblemSolveTour from './components/ProblemSolveTour';
import IdeTour from './components/IdeTour';
import InterviewPrepPage from './pages/InterviewPrepPage';
import InterviewPrepTopicRedirect from './pages/InterviewPrepTopicPage';
import ScrollToTop from './components/ScrollToTop';
import NeetTestsPage from './pages/NeetTestsPage';
import NeetTestLive from './pages/NeetTestLive';
import NeetTestResult from './pages/NeetTestResult';
import NeetTestLeaderboard from './pages/NeetTestLeaderboard';
import ManageNeetTests from './pages/admin/ManageNeetTests';
import NeetTestFormPage from './pages/admin/NeetTestFormPage';
import NeetTestAttemptsPage from './pages/admin/NeetTestAttemptsPage';
import DsaToolkitPage from './pages/DsaToolkitPage';
import DsaToolkitPrivacyPage from './pages/DsaToolkitPrivacyPage';
import { ChatProvider } from './common/ChatContext';

const ACCENT = '#137274';

/** Keep query/hash when renaming public URLs for SEO. */
const RedirectPreserve = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

const RedirectLiveCodingRoom = () => {
  const { roomId } = useParams();
  const { search, hash } = useLocation();
  return (
    <Navigate
      to={`/live-coding/${encodeURIComponent(roomId)}${search}${hash}`}
      replace
    />
  );
};

const App = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkMode = useSelector((state) => state.theme.isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  // Real browser fullscreen (e.g. during a NEET test) still renders our own
  // page chrome inside the fullscreen viewport — the Fullscreen API only
  // hides the browser's own UI, not this app's Navbar/Footer. Hide those
  // ourselves whenever fullscreen is actually active.
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const rawUser = localStorage.getItem('user');
      if (!token || !rawUser) return;
      const user = JSON.parse(rawUser);
      if (user && typeof user === 'object') {
        dispatch(login(user));
      }
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }, [dispatch]);

  const handleFeedbackOpen = () => setFeedbackOpen(true);
  const handleFeedbackClose = () => setFeedbackOpen(false);
  const location = useLocation();
  const isImmersivePage =
    location.pathname.startsWith('/live-coding') ||
    /^\/contests\/[^/]+\/live\/?$/.test(location.pathname) ||
    /^\/neet-tests\/[^/]+\/live\/?$/.test(location.pathname);
  const isProblemSolvePage =
    /^\/problems\/[^/]+\/?$/.test(location.pathname) ||
    /^\/admin\/problems\/[^/]+\/?$/.test(location.pathname) ||
    /^\/contests\/[^/]+\/live\/?$/.test(location.pathname);
  const isSolveWorkspace = isImmersivePage || isProblemSolvePage || isFullscreen;
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isInterviewBook =
    pathParts[0] === 'interview-prep' && pathParts.length === 2;

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <ScrollToTop />
      <ToastContainer
        autoClose={2000}
        position="bottom-left"
        toastClassName="app-toast"
        hideProgressBar
        closeButton={false}
      />

      {/* Root layout with flexbox */}
      <ChatProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          ...(isSolveWorkspace
            ? { height: '100dvh', overflow: 'hidden' }
            : null),
        }}
      >
        {!isFullscreen && (
          <Box sx={{ flexShrink: 0 }}>
            <Navbar />
          </Box>
        )}

        {/* Main content area with padding to prevent footer overlap */}
        <Box
          id="app-main-scroll"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: isSolveWorkspace ? 'hidden' : isInterviewBook ? 'visible' : 'auto',
            display: isSolveWorkspace || isInterviewBook ? 'flex' : 'block',
            flexDirection: 'column',
            paddingBottom: 0,
          }}
        >
          <Box
            sx={{
              flex: isSolveWorkspace ? 1 : undefined,
              minHeight: isSolveWorkspace ? 0 : undefined,
              display: isSolveWorkspace ? 'flex' : 'block',
              flexDirection: 'column',
            }}
          >
          <Routes>
            <Route path="/" element={<ProblemsPage />} />
            <Route path="/problems" element={<ProblemsPage />} />
            <Route path="/problems/:slug" element={<ProblemPage />} />
            <Route path="/admin/problems/:slug" element={<ProblemPage isAdmin={true} />} />

            {/* Practice Track */}
            <Route path="/practice-track" element={<ProgramsPage />} />
            <Route path="/my-programs" element={<RedirectPreserve to="/practice-track" />} />

            <Route path="/ide/:link" element={<CompilerPage />} />
            <Route path="/ide" element={<CompilerPage />} />
            <Route path="/programs" element={<DSACoursePage />} />
            <Route path="/challenges/75-hard-coding-challenge" element={<ChallengeDetailsPage />} />
            <Route path="/contests" element={<ContestsPage />} />
            <Route path="/contests/details/:contestId" element={<ContestInfoPage />} />
            <Route path="/contests/:contestId/result" element={<ContestResult />} />
            <Route path="/contests/:contestId/live" element={<LiveContest />} />
            <Route path="/neet-tests" element={<NeetTestsPage />} />
            <Route path="/neet-tests/:testId/live" element={<NeetTestLive />} />
            <Route path="/neet-tests/:testId/result" element={<NeetTestResult />} />
            <Route path="/neet-tests/:testId/leaderboard" element={<NeetTestLeaderboard />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/user/:username" element={<UserProfilePage />} />
            <Route path="/profile" element={<UserProfilePage />} />
            <Route path="/saved" element={<SavedProblemsPage />} />

            {/* 1:1 Mentorship + mock interviews */}
            <Route path="/mentorship" element={<BookingPage />} />
            <Route path="/mock-interview" element={<BookingPage />} />
            <Route
              path="/schedule-mock-interviews"
              element={<RedirectPreserve to="/mentorship" />}
            />
            <Route
              path="/mock-interviews"
              element={<RedirectPreserve to="/mock-interview" />}
            />

            <Route path="/careers" element={<CareersPage />} />
            <Route path="/interview-prep" element={<InterviewPrepPage />} />
            <Route path="/interview-prep/:trackId" element={<InterviewPrepPage />} />
            <Route path="/interview-prep/:trackId/:topicId" element={<InterviewPrepTopicRedirect />} />

            {/* Live coding */}
            <Route path="/live-coding" element={<LiveCodingRoom />} />
            <Route path="/live-coding/:roomId" element={<LiveCodingRoom />} />
            <Route
              path="/live-coding-room"
              element={<RedirectPreserve to="/live-coding" />}
            />
            <Route
              path="/live-coding-room/:roomId"
              element={<RedirectLiveCodingRoom />}
            />

            {/* Admin Routes */}
            <Route path="/admin/manage-problems" element={<ManageProblems />} />
            <Route path="/admin/create-problem" element={<ProblemFormPage />} />
            <Route path="/admin/manage-contests" element={<ManageContests />} />
            <Route path="/admin/manage-neet-tests" element={<ManageNeetTests />} />
            <Route path="/admin/neet-tests/new" element={<NeetTestFormPage />} />
            <Route path="/admin/neet-tests/:id/edit" element={<NeetTestFormPage />} />
            <Route path="/admin/neet-tests/:id/attempts" element={<NeetTestAttemptsPage />} />
            <Route path="/admin/manage-users" element={<ManageUsers />} />
            <Route path="/admin/problem-feedback" element={<ManageProblemFeedback />} />
            <Route path="/admin/manage-careers" element={<ManageCareers />} />
            <Route path="/admin/broadcast-email" element={<AdminBroadcastEmail />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/edit-problem/:slug" element={<ProblemFormPage />} />

            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/auth/github/callback" element={<GithubCallback />} />

            <Route path="/dsa-toolkit" element={<DsaToolkitPage />} />
            <Route path="/dsa-toolkit/privacy" element={<DsaToolkitPrivacyPage />} />

            <Route path="*" element={<ProblemsPage />} />
          </Routes>
          </Box>
        </Box>

        {/* Footer hidden while solving, in live rooms, and in the interview-prep reader (full-height book UI) */}
        {!isSolveWorkspace && !isInterviewBook && <Footer sx={{ flexShrink: 0 }} />}

        {/* Feedback launcher — side tab (desktop) / FAB (mobile); hidden in immersive rooms */}
        {!isSolveWorkspace &&
          (isMobile ? (
            <Tooltip title="Send feedback" placement="left">
              <Fab
                color="primary"
                aria-label="Send feedback"
                onClick={handleFeedbackOpen}
                size="medium"
                sx={{
                  position: 'fixed',
                  right: 16,
                  bottom: 20,
                  zIndex: 1200,
                  bgcolor: ACCENT,
                  boxShadow: `0 10px 28px ${alpha(ACCENT, 0.35)}`,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                <ChatBubbleOutlineRoundedIcon />
              </Fab>
            </Tooltip>
          ) : (
            <Box
              component="button"
              type="button"
              onClick={handleFeedbackOpen}
              aria-label="Send feedback"
              sx={{
                position: 'fixed',
                right: 0,
                top: '42%',
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                px: 1.1,
                py: 1.75,
                m: 0,
                border: 'none',
                borderTopLeftRadius: 12,
                borderBottomLeftRadius: 12,
                cursor: 'pointer',
                font: 'inherit',
                fontWeight: 800,
                fontSize: '0.78rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#fff',
                bgcolor: ACCENT,
                boxShadow: `-4px 8px 24px ${alpha(ACCENT, 0.35)}`,
                transition: 'background 0.15s, padding 0.15s',
                '&:hover': {
                  bgcolor: '#0f5c5e',
                  pr: 1.35,
                },
              }}
            >
              <ChatBubbleOutlineRoundedIcon
                sx={{
                  fontSize: 16,
                  transform: 'rotate(90deg)',
                  mb: 0.5,
                }}
              />
              Feedback
            </Box>
          ))}

        <FeedbackForm open={isFeedbackOpen} handleClose={handleFeedbackClose} />
        <ProductTour />
        <ProblemsPageTour />
        <ProblemSolveTour />
        <ContestsPageTour />
        <IdeTour />
        <LiveCodingPromoModal />
      </Box>
      </ChatProvider>
    </ThemeProvider>
  );
};

export default App;
